import { makeAutoObservable } from "mobx";
import { ExpansionStore } from "./expansion";
import { NodeId, ObjectNode, TreeNode, isObjectNode } from "./node";

/**
 * Хранилище дерева: узлы и связи «владелец → дети».
 *
 * Раскрытие живёт отдельно, но забывается вместе с узлом: удалённая ветвь не
 * должна остаться в списке раскрытых.
 *
 * Ключевое отличие от v1: дети хранятся отдельно от узла. Поэтому
 * «дети не загружены» (нет записи в карте) и «детей нет» (пустой массив) —
 * различимые состояния, а не соглашение про `undefined` внутри узла.
 *
 * Порядок узлов store не назначает: он принимает его от вызывающей стороны.
 * Сортировка зависит от языка интерфейса, а домен про язык не знает.
 */
export class TreeStore {
  constructor(private readonly expansion: ExpansionStore) {
    makeAutoObservable<TreeStore, "expansion">(this, { expansion: false });
  }

  private readonly nodes = new Map<NodeId, TreeNode>();

  private readonly childIds = new Map<NodeId, NodeId[]>();

  private rootIdList: NodeId[] = [];

  get rootIds(): readonly NodeId[] {
    return this.rootIdList;
  }

  node(id: NodeId): TreeNode | undefined {
    return this.nodes.get(id);
  }

  /**
   * Узлы, чьи дети уже загружены.
   *
   * Этим списком управляем мы, а не Ant Tree: у дерева свой учёт, и при
   * неудачной загрузке оно рано или поздно пометит ветвь загруженной само -
   * после чего перестанет её запрашивать навсегда.
   */
  get loadedIds(): NodeId[] {
    return Array.from(this.childIds.keys());
  }

  /** `undefined` означает «дети не загружены», пустой массив — «детей нет». */
  children(id: NodeId): readonly NodeId[] | undefined {
    return this.childIds.get(id);
  }

  /**
   * Замена списка корней с сохранением всего загруженного.
   *
   * Дети привязаны к идентификатору узла, а идентификатор объекта стабилен,
   * поэтому уже загруженное поддерево переживает обновление каталога.
   * Исчезнувшие корни удаляются вместе с поддеревом.
   */
  setRoots(roots: TreeNode[]) {
    this.rootIdList = this.replaceLevel(this.rootIdList, roots);
  }

  /**
   * Дети узла. Уцелевшие узлы сохраняются вместе со своим поддеревом и
   * раскрытием: повторная загрузка объекта не должна схлопывать раскрытые ветки.
   */
  setChildren(ownerId: NodeId, children: TreeNode[]) {
    const current = this.childIds.get(ownerId) ?? [];
    this.childIds.set(ownerId, this.replaceLevel(current, children));
  }

  /** Удаление узла вместе с поддеревом, раскрытием и ссылкой у владельца. */
  remove(id: NodeId) {
    const node = this.nodes.get(id);
    this.forget(id);
    this.rootIdList = this.rootIdList.filter((it) => it !== id);
    if (node && !isObjectNode(node)) {
      const siblings = this.childIds.get(node.ownerId);
      if (siblings) {
        this.childIds.set(
          node.ownerId,
          siblings.filter((it) => it !== id),
        );
      }
    }
  }

  /** Забыть дерево целиком: смена пользователя, а не обновление данных. */
  clear() {
    this.nodes.clear();
    this.childIds.clear();
    this.expansion.forget(this.rootIdList);
    this.rootIdList = [];
  }

  owner(id: NodeId): TreeNode | undefined {
    const node = this.nodes.get(id);
    if (!node || isObjectNode(node)) return undefined;
    return this.nodes.get(node.ownerId);
  }

  /** Объект, которому принадлежит узел. Для самого объекта — он сам. */
  ownerObject(id: NodeId): ObjectNode | undefined {
    let current = this.nodes.get(id);
    while (current && !isObjectNode(current)) {
      current = this.nodes.get(current.ownerId);
    }
    return current;
  }

  /** Узел и всё его поддерево. Используется при удалении и очистке выбора. */
  subtreeIds(id: NodeId): NodeId[] {
    const result: NodeId[] = [];
    const visit = (nodeId: NodeId) => {
      result.push(nodeId);
      (this.childIds.get(nodeId) ?? []).forEach(visit);
    };
    visit(id);
    return result;
  }

  /** Добавление одного ребёнка: новый черновик атрибута или группы. */
  appendChild(ownerId: NodeId, child: TreeNode) {
    this.nodes.set(child.id, child);
    this.childIds.set(ownerId, [
      ...(this.childIds.get(ownerId) ?? []),
      child.id,
    ]);
  }

  /**
   * Замена одного уровня: пришедшие узлы обновляются, исчезнувшие забываются
   * вместе с поддеревом. Общая часть setRoots и setChildren.
   */
  private replaceLevel(
    currentIds: readonly NodeId[],
    nodes: TreeNode[],
  ): NodeId[] {
    const arrived = new Set(nodes.map(({ id }) => id));
    currentIds
      .filter((id) => !arrived.has(id))
      .forEach((id) => this.forget(id));
    nodes.forEach((node) => this.nodes.set(node.id, node));
    return nodes.map(({ id }) => id);
  }

  /** Забыть поддерево целиком, не трогая ссылку у владельца. */
  private forget(id: NodeId) {
    const ids = this.subtreeIds(id);
    ids.forEach((nodeId) => {
      this.nodes.delete(nodeId);
      this.childIds.delete(nodeId);
    });
    this.expansion.forget(ids);
  }
}
